import { Link } from '@tanstack/react-router';
import { TableCell, TableRow } from '@/shared/components';
import type { CalendarDayCell, ChannexChannelResource, PropertyResponse } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';
import { OccupancyStrip } from '../OccupancyStrip';
import { OtaBadges } from '../OtaBadges';
import { CoverThumb } from './CoverThumb';
import { ListStatusPill } from './ListStatusPill';

interface PropertyTableRowProps {
  property: PropertyResponse;
  occupancyCells: CalendarDayCell[] | undefined;
  stripFrom: string;
  channels: ChannexChannelResource[];
  /** Filter, Suche und Seite der Liste — die Detailansicht braucht sie für ihre Pfeile (F4). */
  listContext: Record<string, unknown>;
}

export function PropertyTableRow({
  property: p,
  occupancyCells,
  stripFrom,
  channels,
  listContext,
}: PropertyTableRowProps) {
  return (
    <TableRow>
      <TableCell>
        <Link
          to="/properties/$id"
          params={{ id: p.id }}
          search={listContext as never}
          className="hover:text-sage flex items-center gap-3 transition-colors"
        >
          <CoverThumb property={p} />
          <div className="min-w-0">
            <span className="text-ink block font-serif text-base">
              {p.adminDisplayName ?? p.name}
            </span>
            {p.adminDisplayName && p.adminDisplayName !== p.name && (
              <span className="text-ink-faint mt-0.5 block truncate text-xs">Intern: {p.name}</span>
            )}
            <span className="text-ink-soft mt-0.5 block truncate text-xs">
              {p.address.street || `${p.address.city}, ${p.address.countryCode}`}
            </span>
          </div>
        </Link>
      </TableCell>
      <TableCell>
        {p.address.city} <span className="text-ink-faint">· {p.address.countryCode}</span>
      </TableCell>
      <TableCell>
        <OccupancyStrip cells={occupancyCells} fromDate={stripFrom} />
      </TableCell>
      <TableCell className="text-right">
        <span className="text-sm tabular-nums">{formatMoney(p.cleaningFee, p.currency)}</span>
      </TableCell>
      <TableCell>
        <ListStatusPill status={p.status} />
      </TableCell>
      <TableCell>
        <OtaBadges
          channexPropertyId={p.channexPropertyId}
          unitPublished={p.channexUnitPublished}
          publicationHealth={p.publicationHealth}
          channels={channels}
        />
      </TableCell>
    </TableRow>
  );
}
